import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GlossaryTermPage = () => {
  const { slug } = useParams();
  const [term, setTerm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/glossary/${slug}`);
        if (!cancelled) setTerm(res.data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !term) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Term Not Found</h1>
        <Link to="/glossary" className="btn-editorial">Browse the Glossary</Link>
      </div>
    );
  }

  return (
    <div data-testid={`glossary-term-page-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={`${term.term} — Jewelry Glossary · Jewelry Refined`}
        description={term.definition.slice(0, 158)}
        url={`/glossary/${slug}`}
        type="article"
      />
      <SchemaOrg
        type="definedterm"
        data={{ term: term.term, definition: term.definition, url: `/glossary/${slug}` }}
      />
      <SchemaOrg
        type="breadcrumb"
        data={{ items: [
          { name: 'Home', url: '/' },
          { name: 'Glossary', url: '/glossary' },
          { name: term.term, url: `/glossary/${slug}` },
        ]}}
      />

      <article className="container-editorial py-16 lg:py-24 max-w-3xl">
        <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
          <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/glossary" className="hover:text-[#111] transition-colors">Glossary</Link>
          <span>/</span>
          <span className="text-[#111]">{term.category}</span>
        </nav>

        <p className="overline overline-gold mb-6">{term.category}</p>
        <h1 className="font-heading font-light text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#111] mb-10">
          {term.term}
        </h1>

        <div className="text-xl lg:text-2xl text-[#555] leading-relaxed font-light first-letter:font-heading first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-[#111]">
          {term.definition}
        </div>

        {term.related && term.related.length > 0 && (
          <div className="mt-16 pt-12 border-t border-[#E5E0D8]" data-testid="related-terms">
            <p className="overline overline-gold mb-6 inline-flex items-center gap-2">
              <BookOpen className="w-3 h-3" strokeWidth={1.5} /> Related Terms
            </p>
            <div className="flex flex-wrap gap-3">
              {term.related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/glossary/${r.slug}`}
                  className="text-xs tracking-[0.15em] uppercase border border-[#E5E0D8] hover:border-[#111] px-3 py-2 text-[#555] hover:text-[#111] transition-colors"
                >
                  {r.term}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 pt-12 border-t border-[#E5E0D8]">
          <Link to="/glossary" className="group inline-flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#111]">
            <ArrowLeft className="w-4 h-4 text-[#C69C6D] group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
            All Glossary Terms
          </Link>
        </div>
      </article>
    </div>
  );
};

export default GlossaryTermPage;
